import { resolveErrorText } from '@/api/errors';
import { authFetch } from '@/auth/token-interceptor';
import { chatErrorText, uploadAttachment } from '@/features/ai-assistant/api/assistant-api';
import {
  rowsAtReadingLine,
  useChangeNavigation,
} from '@/features/ai-changeset/hooks/useChangeNavigation';
import {
  dockPhase,
  lastReplyOf,
  mergeTurnPages,
  type DockSession,
} from '@/features/ai-changeset/utils/dock-history';
import { followDock } from '@/features/ai-changeset/utils/dock-toast';
import { getErrorT, useI18nStore, useT } from '@/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import {
  AI_CHANGESET_TURN_ATTACHMENTS_MAX,
  type AiChangesetView,
  type AiTurnAttachment,
} from '@restaurant/api-contracts';
import { useChat } from '@ai-sdk/react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DefaultChatTransport, type UIMessage } from 'ai';
import { f7 } from 'framework7-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

export type TurnOutcome<TChange> =
  | { applied: true; appliedCount: number; missing: TChange[] }
  | { applied: false; stale: TChange[] };

export interface AiChangesetApi<TChange> {
  load: (before: string | undefined) => Promise<DockSession<TChange> | null>;
  apply: (turnId: string) => Promise<TurnOutcome<TChange>>;
  undo: (turnId: string) => Promise<TurnOutcome<TChange>>;
  discard: (turnId: string) => Promise<unknown>;
}

export interface AiChangesetAdapter<TChange> {
  targets: (changes: TChange[]) => string[];
  labels: (changes: TChange[]) => string[];
  summary: (changes: TChange[]) => string;
}

export interface AiChangesetConfig<TChange> {
  id: string;
  changesSchema: z.ZodType<TChange[]>;
  enabled: boolean;
  turnUrl: string;
  pageName: string;
  fullViewLabel: string | null;
  viewRows: number;
  view?: () => Pick<AiChangesetView, 'sectionId' | 'search'>;
  api: AiChangesetApi<TChange>;
  onApplySettled: () => unknown;
  adapter: AiChangesetAdapter<TChange>;
}

export interface DockAttachment {
  id: string;
  kind: AiTurnAttachment['kind'];
  fileName: string;
  url: string;
}

export interface LiveTurn {
  text: string;
  attachments: DockAttachment[];
  reply: string;
}

export const DOCK_ACCEPTED_FILES = ['image/png', 'image/jpeg', 'image/webp', 'application/pdf'];
const POLL_MS = 2500;

const PROPOSAL_PART = z.object({
  type: z.literal('tool-proposeChanges'),
  state: z.literal('output-available'),
  output: z.object({ changes: z.unknown() }),
});

export function streamedDraft<TChange>(
  messages: UIMessage[],
  changesSchema: z.ZodType<TChange[]>,
): TChange[] | null {
  for (const message of [...messages].reverse()) {
    for (const part of [...message.parts].reverse()) {
      const proposal = PROPOSAL_PART.safeParse(part);
      if (!proposal.success) continue;
      const changes = changesSchema.safeParse(proposal.data.output.changes);
      if (changes.success) return changes.data;
    }
  }
  return null;
}

const textOf = (message: UIMessage | undefined): string =>
  (message?.parts ?? [])
    .flatMap((part) => (part.type === 'text' ? [part.text] : []))
    .join('\n')
    .trim();

export function liveTurnOf(messages: UIMessage[], attachments: DockAttachment[]): LiveTurn | null {
  let userIndex = messages.length - 1;
  while (userIndex >= 0 && messages[userIndex].role !== 'user') userIndex--;
  if (userIndex < 0) return null;
  const answer = messages.slice(userIndex + 1).find((m) => m.role === 'assistant');
  return { text: textOf(messages[userIndex]), attachments, reply: textOf(answer) };
}

export function useAiChangeset<TChange>(config: AiChangesetConfig<TChange>) {
  const t = useT();
  const q = t.aiChangeset;
  const qc = useQueryClient();
  const key = ['ai-changeset', config.id] as const;
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const [staleLabels, setStaleLabels] = useState<string[]>([]);
  const [attachments, setAttachments] = useState<DockAttachment[]>([]);
  const [liveAttachments, setLiveAttachments] = useState<DockAttachment[]>([]);
  const [uploading, setUploading] = useState(0);
  const applyingRef = useRef(false);
  const pendingIds = useRef<string[]>([]);
  const { adapter, api } = config;
  const viewRef = useRef<(() => AiChangesetView) | null>(null);
  const settledRef = useRef(config.onApplySettled);

  const session = useInfiniteQuery({
    queryKey: key,
    enabled: config.enabled,
    initialPageParam: '',
    queryFn: ({ pageParam }) => api.load(pageParam || undefined),
    getNextPageParam: (last) => (last?.hasMore ? last.turns[0]?.id : undefined),
    refetchInterval: (query) => {
      const head = query.state.data?.pages[0];
      return head && (head.busy || head.menuImport?.status === 'reading') ? POLL_MS : false;
    },
  });
  const pages = useMemo(
    () => (session.data?.pages ?? []).flatMap((page) => (page ? [page] : [])),
    [session.data],
  );
  const head = pages[0] ?? null;
  const turns = useMemo(() => mergeTurnPages(pages), [pages]);
  const refetchSession = () => qc.invalidateQueries({ queryKey: key });

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: config.turnUrl,
        fetch: authFetch,
        prepareSendMessagesRequest: ({ messages }) => ({
          body: {
            locale: useI18nStore.getState().locale,
            text: textOf(messages.filter((m) => m.role === 'user').at(-1)),
            view: viewRef.current?.(),
            attachmentIds: pendingIds.current,
          },
        }),
      }),
    [config.turnUrl],
  );
  const chat = useChat({
    id: `ai-changeset:${config.id}`,
    transport,
    onFinish: () => {
      void refetchSession().then(() => {
        chat.setMessages([]);
        setLiveAttachments([]);
      });
    },
    onError: () => void refetchSession(),
  });

  const apply = useMutation({
    mutationFn: api.apply,
    onSettled: () => {
      void refetchSession();
      config.onApplySettled();
    },
  });
  const undo = useMutation({
    mutationFn: api.undo,
    onSettled: () => {
      void refetchSession();
      config.onApplySettled();
    },
  });
  const discard = useMutation({ mutationFn: api.discard, onSettled: () => refetchSession() });

  const streaming = chat.status === 'submitted' || chat.status === 'streaming';
  const liveTurn = useMemo(
    () => liveTurnOf(chat.messages, liveAttachments),
    [chat.messages, liveAttachments],
  );
  const reply = liveTurn ? liveTurn.reply : lastReplyOf(turns);
  const draftTurn = head?.draft ?? null;
  const streamed = useMemo(
    () => streamedDraft(chat.messages, config.changesSchema),
    [chat.messages, config.changesSchema],
  );
  const changes = useMemo(() => streamed ?? draftTurn?.changes ?? [], [streamed, draftTurn]);
  const targets = useMemo(() => adapter.targets(changes), [adapter, changes]);
  const summary = useMemo(() => adapter.summary(changes), [adapter, changes]);
  const draftKey = useMemo(() => JSON.stringify(changes), [changes]);
  const [fullViewFor, setFullViewFor] = useState<string | null>(null);
  const changesOnly =
    config.fullViewLabel !== null && targets.length > 0 && fullViewFor !== draftKey;
  const navigation = useChangeNavigation(
    targets,
    changesOnly ? 'changes' : 'full',
    config.pageName,
  );
  const currentView = (): AiChangesetView => ({
    sectionId: null,
    search: null,
    ...config.view?.(),
    mode: config.fullViewLabel === null ? null : changesOnly ? 'changes' : 'full',
    rowIds: rowsAtReadingLine(config.pageName, config.viewRows),
  });
  useEffect(() => {
    viewRef.current = currentView;
    settledRef.current = config.onApplySettled;
  });

  const importStatus = head?.menuImport?.status ?? null;
  const importTick = head?.menuImport
    ? `${head.menuImport.status}:${head.menuImport.dishesSoFar}`
    : null;
  const lastImportTick = useRef(importTick);
  useEffect(() => {
    const previous = lastImportTick.current;
    if (previous === importTick) return;
    lastImportTick.current = importTick;
    if (previous !== null) settledRef.current();
  }, [importTick]);

  const hasDraft = changes.length > 0 || staleLabels.length > 0;
  const reviewReady = !!draftTurn && !streaming;
  const revealRef = useRef({ navigation, first: targets[0] });
  useEffect(() => {
    revealRef.current = { navigation, first: targets[0] };
  });
  const revealedDraft = useRef<string | null>(null);
  useEffect(() => {
    if (!reviewReady || revealedDraft.current === draftKey) return;
    revealedDraft.current = draftKey;
    requestAnimationFrame(() => {
      const { navigation: nav, first } = revealRef.current;
      if (first) nav.jumpTo(first);
    });
  }, [reviewReady, draftKey]);
  const phase = dockPhase({
    importStatus,
    streaming,
    sessionBusy: !!head?.busy,
    hasDraft,
  });

  const toast = (params: Parameters<typeof f7.toast.create>[0]) => {
    let stop = () => {};
    f7.toast
      .create({
        ...params,
        on: {
          ...params.on,
          open: (t) => {
            stop = followDock(t.el);
          },
          close: () => stop(),
        },
      })
      .open();
  };

  const showError = (error: unknown) =>
    toast({ text: resolveErrorText(error, getErrorT()), closeTimeout: 4000 });

  const addFiles = async (files: File[]) => {
    const accepted = files
      .filter((file) => DOCK_ACCEPTED_FILES.includes(file.type))
      .slice(0, AI_CHANGESET_TURN_ATTACHMENTS_MAX - attachments.length - uploading);
    if (accepted.length === 0 || !restaurantId) return;
    setUploading((n) => n + accepted.length);
    await Promise.all(
      accepted.map(async (file) => {
        try {
          const uploaded = await uploadAttachment(restaurantId, file);
          const kind = file.type === 'application/pdf' ? 'pdf' : 'image';
          setAttachments((current) => [
            ...current,
            {
              id: uploaded.id,
              kind,
              fileName: uploaded.fileName,
              url: kind === 'image' ? URL.createObjectURL(file) : '',
            },
          ]);
        } catch (error) {
          showError(error);
        } finally {
          setUploading((n) => n - 1);
        }
      }),
    );
  };

  const removeAttachment = (id: string) =>
    setAttachments((current) => current.filter((a) => a.id !== id));

  const canSend =
    config.enabled &&
    uploading === 0 &&
    phase !== 'reading' &&
    phase !== 'streaming' &&
    phase !== 'locked';

  const submit = (text: string): boolean => {
    const instruction = text.trim();
    if (!canSend || (!instruction && attachments.length === 0)) return false;
    pendingIds.current = attachments.map((a) => a.id);
    setLiveAttachments(attachments);
    setAttachments([]);
    setStaleLabels([]);
    setFullViewFor(null);
    chat.clearError();
    chat.setMessages([]);
    void chat.sendMessage({ text: instruction });
    return true;
  };

  const undoTurn = (turnId: string) => {
    if (applyingRef.current) return;
    applyingRef.current = true;
    undo.mutate(turnId, {
      onSuccess: (outcome) =>
        toast({
          text: outcome.applied ? q.undoneToast : q.undoStale,
          closeTimeout: 3000,
        }),
      onError: showError,
      onSettled: () => {
        applyingRef.current = false;
      },
    });
  };

  const applyTurn = (turnId: string, count: number) => {
    if (applyingRef.current || streaming) return;
    applyingRef.current = true;
    setStaleLabels([]);
    apply.mutate(turnId, {
      onSuccess: (outcome) => {
        if (!outcome.applied) {
          setStaleLabels(adapter.labels(outcome.stale));
          return;
        }
        setFullViewFor(null);
        if (outcome.missing.length > 0) setStaleLabels(adapter.labels(outcome.missing));
        toast({
          text:
            outcome.missing.length > 0
              ? q.appliedPartialToast(outcome.appliedCount, count)
              : q.appliedToast(outcome.appliedCount),
          closeButton: true,
          closeButtonText: q.undo,
          closeTimeout: 8000,
          on: { closeButtonClick: () => undoTurn(turnId) },
        });
      },
      onError: showError,
      onSettled: () => {
        applyingRef.current = false;
      },
    });
  };

  const applyDraft = () => {
    if (!draftTurn || changes.length === 0) return;
    applyTurn(draftTurn.turnId, changes.length);
  };

  const discardTurn = (turnId: string) => {
    if (discard.isPending || streaming) return;
    discard.mutate(turnId, {
      onSuccess: () => {
        setStaleLabels([]);
        setFullViewFor(null);
      },
      onError: showError,
    });
  };

  const discardDraft = () => {
    if (!draftTurn) {
      setStaleLabels([]);
      return;
    }
    discardTurn(draftTurn.turnId);
  };

  const showChangesOnly = (on: boolean) => {
    if (on === changesOnly) return;
    navigation.holdPosition();
    setFullViewFor(on ? null : draftKey);
  };

  const error = chat.error
    ? (chatErrorText(chat.error) ?? t.aiAssistant.sendFailed)
    : session.error
      ? resolveErrorText(session.error, getErrorT())
      : null;

  return {
    phase,
    turns,
    liveTurn,
    hasMore: session.hasNextPage,
    loadingOlder: session.isFetchingNextPage,
    loadOlder: () => void session.fetchNextPage(),
    loading: session.isLoading,
    menuImport: head?.menuImport ?? null,
    reply,
    busy: streaming,
    lockedByOther: phase === 'locked',
    error,
    changes,
    changeCount: changes.length,
    reviewReady,
    hasDraft,
    summary,
    fullViewLabel: config.fullViewLabel,
    changesOnly,
    showChangesOnly,
    navigation,
    staleLabels,
    applying: apply.isPending || undo.isPending,
    discarding: discard.isPending,
    attachments,
    uploading,
    addFiles,
    removeAttachment,
    canSend,
    submit,
    applyDraft,
    discardDraft,
    applyTurn,
    discardTurn,
    undoTurn,
  };
}

export type AiChangeset<TChange> = ReturnType<typeof useAiChangeset<TChange>>;
export type AiDockControls = Omit<AiChangeset<unknown>, 'changes'>;
